import type React from 'react'
import { usePopper } from 'react-popper'
import type * as PopperJS from '@popperjs/core'
import { useCallback, useRef, useState } from 'react'

// Tooltip Credit: https://github.com/tailwindlabs/headlessui/discussions/425#discussioncomment-3480285

type TooltipProps = {
  label: string
  obvious?: boolean
  enterDelay?: number
  leaveDelay?: number
  placement?: PopperJS.Placement
} & React.HTMLAttributes<HTMLDivElement>

export const Tooltip: React.FC<TooltipProps> = (props) => {
  const {
    label,
    children,
    obvious = false,
    enterDelay = 250,
    leaveDelay = 150,
    placement = 'auto',
  } = props

  const [isOpen, setIsOpen] = useState(false)
  let [referenceElement, setReferenceElement] =
    useState<HTMLSpanElement | null>(null)
  let [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  let { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 5] } },
      {
        name: 'arrow',
        options: {
          padding: 5,
        },
      },
    ],
  })

  let enterTimeout = useRef<NodeJS.Timeout>()
  let leaveTimeout = useRef<NodeJS.Timeout>()
  const handleMouseEnter = useCallback(() => {
    leaveTimeout.current && clearTimeout(leaveTimeout.current)
    enterTimeout.current = setTimeout(() => setIsOpen(true), enterDelay)
  }, [enterDelay])
  const handleMouseLeave = useCallback(() => {
    enterTimeout.current && clearTimeout(enterTimeout.current)
    leaveTimeout.current = setTimeout(() => setIsOpen(false), leaveDelay)
  }, [leaveDelay])

  return (
    <>
      <span
        ref={setReferenceElement}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative ${
          obvious
            ? 'border-b border-blue-500 border-spacing-1 border-dashed cursor-help'
            : ''
        }`}
      >
        {children}
      </span>

      <div
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        className={`transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <span className="flex items-center bg-gray-800 text-white dark:bg-gray-100 dark:text-gray-800 text-sm p-2 rounded-sm shadow-md max-w-xs">
          {label}
        </span>
      </div>
    </>
  )
}
